import {
  ReactElement, ReactNode, useEffect, useMemo, useRef, useState,
} from 'react';
import type ApexCharts from 'apexcharts';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { AuxPlotSeries, TraverseSegment } from './auxSensorChannels';
import {
  AUX_COLORS, AUX_FIXED_TOOLTIP, auxBaseOptions, timeAxisLabel, valueAxisLabel,
} from './auxPlotStyle';
import AuxPlotCard from './AuxPlotCard';

interface AuxSensorPlotProps {
  title: string;
  unit: string;
  series: AuxPlotSeries[];
  isLoading?: boolean;
  /** When true, render a disabled placeholder instead of a chart. */
  inactive?: boolean;
  /** Optional control rendered to the right of the title (e.g. a mode toggle). */
  headerExtra?: ReactNode;
  /** Optional full-width control row below the header (e.g. the time-offset slider). */
  controls?: ReactNode;
  /** Shaded start/stop bands for each traverse (shown in Traverse mode). */
  traverseSegments?: TraverseSegment[];
  /** Render per-series visibility checkboxes above the chart. */
  seriesToggles?: boolean;
  /**
   * Show the legend even with a single series. ApexCharts hides it by
   * default, which is right when the one label is a positional placeholder
   * ("Power Meter 1") and wrong when it names an actual device — set this
   * when the run's records identified the sensor.
   */
  nameSingleSeries?: boolean;
  /**
   * Right-hand y-axis for series flagged `secondaryAxis` (e.g. a phase
   * angle riding on a speed plot). Rendered only while such a series is
   * visible; the remaining series keep the primary axis.
   */
  secondaryAxis?: { title: string; unit: string; min?: number; max?: number; tickAmount?: number };
}

export type { AuxPlotSeries };

const CHART_HEIGHT = 360;

/**
 * A single line chart overlaying N same-type sensor instances on a shared
 * relative-time x-axis. Used three times by the Aux Sensor tab (power, wheel
 * speed, dynamic pressure). The curve is drawn `straight` (not smoothed) so the
 * raw samples are shown honestly for cross-sensor comparison.
 */
export default function AuxSensorPlot({
  title,
  unit,
  series,
  isLoading = false,
  inactive = false,
  headerExtra,
  controls,
  traverseSegments,
  seriesToggles = false,
  nameSingleSeries = false,
  secondaryAxis,
}: AuxSensorPlotProps): ReactElement {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const toggleSeries = (key: string) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
  const chartRef = useRef<ApexCharts | null>(null);

  const dataSeries = useMemo(
    () => series.filter((s) => s.points.some((p) => p[1] != null)),
    [series],
  );

  // ApexCharts addresses series by NAME, not index — `hideSeries`,
  // `showSeries` and the y-axis `seriesName` binding all resolve a label and
  // take the first match. Two instances can carry the same label (the naming
  // helper falls back to a bare role when a run identifies neither device),
  // and the first match then answers for both: unticking one collapses the
  // other, silently, because the miss is caught and dropped. So the name the
  // chart sees is made unique here, and the checkbox row shows that same name
  // — the two must not be able to disagree.
  //
  // Numbered over ALL the series, not the data-bearing ones: a name is the
  // identity the chart, the checkbox label and the collapse state are all
  // keyed on, so it must not shift when an unrelated sibling turns out to
  // have no points. Numbering over `dataSeries` renamed the survivor of an
  // empty first instance from "Wheel Speed (2)" to "Wheel Speed", which
  // changed a visible label under the reader and handed
  // `reconcileCollapsedByName` a collapse recorded against the old name.
  const seriesNames = useMemo(() => {
    const seen = new Map<string, number>();
    return new Map(series.map((s) => {
      const nth = (seen.get(s.label) ?? 0) + 1;
      seen.set(s.label, nth);
      return [s.key, nth === 1 ? s.label : `${s.label} (${nth})`] as const;
    }));
  }, [series]);

  const seriesName = (s: AuxPlotSeries) => seriesNames.get(s.key) ?? s.label;

  // Colours are assigned over ALL data-bearing series, so a series keeps
  // its colour when others are toggled off.
  const seriesColors = useMemo(() => {
    let lineIdx = 0;
    return new Map(dataSeries.map((s) => [
      s.key,
      s.markersOnly ? AUX_COLORS.markersOnly : AUX_COLORS.series[lineIdx++ % AUX_COLORS.series.length],
    ]));
  }, [dataSeries]);

  // EVERY data-bearing series is handed to the chart, including the ones the
  // checkboxes have turned off, and the options below are likewise built over
  // all of them. That is deliberate: ApexCharts throws the pan/zoom window
  // away whenever it is given a new options object (`updateOptions` calls
  // `revertDefaultAxisMinMax`, and react-apexcharts always folds the series
  // into that call), so filtering here reset the view on every tick of a
  // checkbox. Holding both props still makes the toggle a no-op for the
  // chart, and visibility is applied to the live chart instead — see the
  // effect below.
  //
  // Mixed chart: a `markersOnly` series is a real scatter series — a
  // zero-width line with per-series marker sizes does not reliably render
  // its points in ApexCharts, a scatter series always does (and shows in
  // the legend). Null gap points are meaningless for scatter, drop them.
  const plotSeries = useMemo(
    () => dataSeries.map((s) => ({
      name: seriesName(s),
      type: s.markersOnly ? 'scatter' : 'line',
      data: s.markersOnly ? s.points.filter((p) => p[1] != null) : s.points,
    })),
    [dataSeries, seriesNames],
  );

  const options = useMemo<ApexOptions>(() => {
    const base = auxBaseOptions(CHART_HEIGHT);
    // Each axis formatter is bound to its own position in the `yaxis` array,
    // which is how ApexCharts keys the tick scale it reads for its decimals.
    const primaryYaxis = (axisIndex = 0) => ({
      title: { text: `${title} (${unit})`, style: { fontSize: '13px', fontWeight: 600 } },
      labels: { formatter: valueAxisLabel(axisIndex, 1) },
    });
    // With a secondary axis, ApexCharts needs one yaxis entry per series;
    // `seriesName` binds every entry of a side to its first series so each
    // side shares one scale, and only that first entry draws the axis. An
    // axis whose every series is hidden is dropped by ApexCharts itself
    // (`isYAxisHidden` → `yAxisAllSeriesCollapsed`), so the right-hand axis
    // still comes and goes with its series.
    let yaxis: ApexOptions['yaxis'] = primaryYaxis();
    const firstSecondary = secondaryAxis
      ? dataSeries.find((s) => s.secondaryAxis) : undefined;
    const firstPrimary = dataSeries.find((s) => !s.secondaryAxis);
    if (secondaryAxis && firstSecondary) {
      const secondary = (axisIndex: number) => ({
        opposite: true,
        min: secondaryAxis.min,
        max: secondaryAxis.max,
        tickAmount: secondaryAxis.tickAmount,
        title: {
          text: `${secondaryAxis.title} (${secondaryAxis.unit})`,
          style: { fontSize: '13px', fontWeight: 600 },
        },
        labels: { formatter: valueAxisLabel(axisIndex, 0) },
      });
      yaxis = dataSeries.map((s, i) => (s.secondaryAxis
        ? { ...secondary(i), seriesName: seriesName(firstSecondary), show: s.key === firstSecondary.key }
        : {
          ...primaryYaxis(i),
          seriesName: firstPrimary ? seriesName(firstPrimary) : undefined,
          show: s.key === firstPrimary?.key,
        }));
    }
    return {
      ...base,
      annotations: {
        xaxis: (traverseSegments ?? []).map((seg) => ({
          x: seg.startS,
          x2: seg.endS,
          fillColor: '#3B82F6',
          opacity: 0.07,
          label: {
            text: `T${seg.lap}`,
            position: 'top' as const,
            style: { color: '#1E3A8A', background: '#EFF6FF', fontSize: '10px' },
          },
        })),
      },
      chart: { ...base.chart, type: 'line', zoom: { enabled: true, type: 'xy', autoScaleYaxis: false } },
      noData: { text: 'All series hidden — tick a checkbox above to plot one.' },
      colors: dataSeries.map((s) => seriesColors.get(s.key) ?? AUX_COLORS.markersOnly),
      stroke: { curve: 'straight', width: dataSeries.map((s) => (s.markersOnly ? 0 : 1.5)) },
      markers: { size: dataSeries.map((s) => (s.markersOnly ? 3 : 0)), strokeWidth: 0 },
      // The checkbox row IS a legend (same labels, same colour dots, plus
      // the toggle) — showing both duplicates it.
      legend: {
        ...base.legend,
        show: !(seriesToggles && dataSeries.length > 1),
        showForSingleSeries: nameSingleSeries,
      },
      xaxis: {
        type: 'numeric',
        title: { text: 'Time (seconds)', style: { fontSize: '13px', fontWeight: 600 } },
        // Decimals follow the zoom — a sub-second window would otherwise
        // print the same rounded second on every tick.
        labels: { formatter: timeAxisLabel },
      },
      yaxis,
      tooltip: {
        shared: true,
        fixed: AUX_FIXED_TOOLTIP,
        x: { formatter: (value: number) => `${value.toFixed(2)} s` },
        y: dataSeries.map((s) => ({
          formatter: (value: number) => (value != null
            ? `${value.toFixed(2)} ${s.secondaryAxis && secondaryAxis ? secondaryAxis.unit : unit}`
            : ''),
        })),
      },
    };
  }, [title, unit, traverseSegments, seriesColors, secondaryAxis,
    seriesToggles, nameSingleSeries, dataSeries, seriesNames]);

  // Apply the checkbox state to the live chart rather than by rebuilding it.
  // `hideSeries`/`showSeries` are the same collapse path the chart's own
  // legend uses, which re-renders through `updateSeries` and so leaves the
  // pan/zoom window alone.
  //
  // The whole set is re-asserted on every run, with no "what did I last
  // apply?" cache in front of it, for two reasons. ApexCharts already holds
  // that state: both `showSeries` and `hideSeries` look the series up with
  // `isSeriesHidden` and return without touching the chart when it is already
  // in the asked-for state, so re-asserting an unchanged set costs one name
  // lookup per series and no render. And a cache here would be wrong as often
  // as it was cheap: collapsed state SURVIVES a series-set change, because the
  // `updateOptions` path react-apexcharts uses calls `reconcileCollapsedByName`
  // and re-attaches the collapse to the matching name. A cache that assumed a
  // new series set meant a cleared collapse would skip the `showSeries` for a
  // series the user had just re-ticked, leaving it invisible with its checkbox
  // reading ticked.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    dataSeries.forEach((s) => {
      // ApexCharts keys these by series NAME and dereferences the lookup
      // without a null check, so a name it cannot find would throw.
      try {
        if (hidden.has(s.key)) chart.hideSeries(seriesName(s));
        else chart.showSeries(seriesName(s));
      } catch {
        // Series not rendered under that name — the next render re-applies.
      }
    });
  }, [dataSeries, hidden, seriesNames]);

  if (inactive) {
    return (
      <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md cr-opacity-70">
        <div className="cr-mb-2 cr-flex cr-items-center cr-gap-2">
          <h2 className="cr-text-lg cr-font-semibold cr-text-gray-400">{title}</h2>
          <span
            className="cr-rounded cr-bg-gray-100 cr-px-2 cr-py-0.5 cr-text-xs
              cr-font-medium cr-text-gray-400"
          >
            Coming soon
          </span>
        </div>
        <div className="cr-flex cr-h-32 cr-items-center cr-justify-center cr-text-sm cr-text-gray-400">
          {title} comparison is not available yet.
        </div>
      </div>
    );
  }

  return (
    <AuxPlotCard
      title={title}
      headerExtra={headerExtra}
      controls={controls}
      isLoading={isLoading}
      loadingLabel={`Loading ${title.toLowerCase()}…`}
      isEmpty={dataSeries.length === 0}
      emptyMessage={`No ${title.toLowerCase()} data available for this run.`}
    >
      {seriesToggles && dataSeries.length > 1 && (
        <div className="cr-mb-2 cr-flex cr-flex-wrap cr-gap-x-4 cr-gap-y-1">
          {dataSeries.map((s) => (
            <label
              key={s.key}
              className="cr-flex cr-cursor-pointer cr-items-center cr-gap-1.5 cr-text-xs cr-text-gray-600"
            >
              <input
                type="checkbox"
                checked={!hidden.has(s.key)}
                onChange={() => toggleSeries(s.key)}
              />
              <span
                className="cr-inline-block cr-h-2 cr-w-2 cr-rounded-full"
                style={{ backgroundColor: seriesColors.get(s.key) }}
              />
              {seriesName(s)}
            </label>
          ))}
        </div>
      )}
      {/* The chart stays mounted even with every series hidden — unmounting it
          would destroy the pan/zoom window this component works to keep. That
          state draws ApexCharts' own `noData` text instead. */}
      <ReactApexChart
        chartRef={chartRef}
        options={options}
        series={plotSeries}
        type="line"
        height={CHART_HEIGHT}
      />
    </AuxPlotCard>
  );
}
